import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, FileText } from 'lucide-react';
import Pagination from '../components/Pagination';

const DocumentsPage = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [canApproveMap, setCanApproveMap] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, [page, statusFilter]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 10 };
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API}/documents`, { params });
      setDocuments(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);

      // Check approval permissions for each pending document
      const approvalChecks = {};
      for (const doc of response.data.items) {
        if (doc.status === 'pending') {
          try {
            const canApproveRes = await axios.get(`${API}/documents/${doc.id}/can-approve`);
            approvalChecks[doc.id] = canApproveRes.data.can_approve;
          } catch {
            approvalChecks[doc.id] = false;
          }
        }
      }
      setCanApproveMap(approvalChecks);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/documents/${deleteId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(parseApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const handleApprove = async (docId, action) => {
    try {
      await axios.post(`${API}/documents/${docId}/approve`, { action, comment: '' });
      toast.success(action === 'approve' ? 'Document approved' : 'Document rejected');
      fetchDocuments();
    } catch (error) {
      toast.error(parseApiError(error));
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const title = doc.data?.judul_dokumen || '';
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return `px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {t('documents')}
          </h1>
          <p className="text-gray-500 mt-1">{total} {t('entries')}</p>
        </div>
        <Button 
          onClick={() => navigate('/documents/new')}
          className="gap-2"
          data-testid="create-document-btn"
        >
          <Plus size={18} />
          {t('create')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={`${t('search')}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="draft">{t('draft')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="approved">{t('approved')}</SelectItem>
                <SelectItem value="rejected">{t('rejected')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('noResults')}</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-600">Title</TableHead>
                  <TableHead className="font-semibold text-gray-600">Form Type</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('status')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdBy')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdAt')}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} className="hover:bg-gray-50" data-testid={`document-row-${doc.id}`}>
                    <TableCell className="font-medium">
                      {doc.data?.judul_dokumen || `Document #${doc.id.slice(0, 8)}`}
                    </TableCell>
                    <TableCell>{doc.form_config_name || '-'}</TableCell>
                    <TableCell>
                      <span className={getStatusBadge(doc.status)}>
                        {t(doc.status)}
                      </span>
                    </TableCell>
                    <TableCell>{doc.creator_name || '-'}</TableCell>
                    <TableCell>{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Approval buttons - only show when user can approve */}
                        {doc.status === 'pending' && canApproveMap[doc.id] && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(doc.id, 'approve')}
                              data-testid={`approve-btn-${doc.id}`}
                            >
                              <CheckCircle size={16} className="mr-1" />
                              {t('approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleApprove(doc.id, 'reject')}
                              data-testid={`reject-btn-${doc.id}`}
                            >
                              <XCircle size={16} className="mr-1" />
                              {t('reject')}
                            </Button>
                          </>
                        )}
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`doc-menu-${doc.id}`}>
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/documents/${doc.id}`)}>
                              <Eye size={14} className="mr-2" />
                              {t('view')}
                            </DropdownMenuItem>
                            {(doc.status === 'draft' || doc.status === 'rejected') && (
                              <DropdownMenuItem onClick={() => navigate(`/documents/${doc.id}`)}>
                                <Edit size={14} className="mr-2" />
                                {t('edit')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => setDeleteId(doc.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} className="mr-2" />
                              {t('delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the document.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentsPage;
